// ./src/components/features/Inventory/InventoryItemEditDrawer/InventoryItemEditDrawer.tsx
//
// Create/edit drawer for an inventory item (account-level catalog entry — e.g. a
// consumable, a piece of gear counted on hand). Self-loading in edit mode via
// recordId; context-or-props resolution; no success toast (the page highlight is the
// confirmation). Fields: name, category (group 'inventory'), sku, unit, unit cost +
// currency, low-stock threshold + tracking, notes, and a repeatable barcodes editor.
// Mirrors RentalAssetEditDrawer.
//
// NOTE: photo (mainImageUploadedFileId) is left out of v1 — see the final report.
//
// ⚠ ORDERED FOR THE PERSON AT THE PALLET (ROADMAP #65 b, owner 2026-09-28). Most items are created
// from an unknown SCAN in the middle of receiving, and the owner's words were that adding one must be
// "very easy and convenient". So a NEW item leads with what receiving needs — name, default vendor,
// the scanned barcode's label and units per pack, unit + cost — and folds the catalog detail
// (category, description, SKU, stock alerts, notes) behind "More details". Editing an existing item
// opens with everything showing. A server refusal on a folded field OPENS the section, so no error can
// sit on a control nobody can see.

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Stack, TextInput, Textarea, Group, Divider, Switch, Skeleton, ActionIcon, Tooltip, Text, Image, Box, Button, Collapse, SimpleGrid } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { InventoryItem, InventoryItemInput, InventoryItemService, InventoryBarcode } from "@/utils/api";
import { getFormErrors, formatErrors } from "@/utils";
import { useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { useOptionalInventoryItemsContext } from "@/contexts";
import { AppDrawer, AppIcon, CategorySelect, CurrencySelector, NumberField } from "@/components/generic";

import { MoneyInput } from "@/components/features/Budget/MoneyInput/MoneyInput";
import { VendorSelect } from "@/components/features/Rentals";
import { InventoryBarcodesEditor } from "../InventoryBarcodesEditor/InventoryBarcodesEditor";

/** Seed a NEW item from a scanned code / product-DB lookup (create mode only). */
export interface InventoryItemPrefill {
  barcodeCode?: string | null;
  name?: string | null;
  description?: string | null;
  /** Free-text product category (a hint shown to the user; NOT our Category id). */
  categoryHint?: string | null;
  /** Product image URL to show as a hint (optional). */
  imageUrl?: string | null;
}

export interface InventoryItemEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  recordId?: string | null;
  /** Pre-fill the name when opening in add mode (e.g. from a "Create X" action). */
  initialName?: string | null;
  /** Seed a new item from a scanned/looked-up product (create mode only). */
  prefill?: InventoryItemPrefill | null;
  /** Event context for account-scoped ops (SKU suggestion). */
  contextEventId?: string | null;
  onCreated?: (item: InventoryItem) => void;
  onUpdated?: (item: InventoryItem) => void;
}

interface FormData {
  name: string;
  categoryId: string | null;
  defaultVendorId: string | null;
  description: string;
  sku: string;
  unit: string;
  unitCost: number | null;
  currencyCode: string | null;
  lowStockThreshold: number | null;
  trackLowStock: boolean;
  minOrderQty: number | null;
  notes: string;
  barcodes: InventoryBarcode[];
  status: number;
}

// NOTE: the lifecycle `status` int is deliberately NOT listed. The drawer
// submits it but has no control for it (no StatusInput here), so listing it
// would park a server refusal in the field map, where nothing renders it — the
// save would fail with no message at all. Unlisted, it falls through to the
// footer prefixed with its own name, which is visible.
const KNOWN_FORM_FIELDS = [
  "name",
  "categoryId",
  "defaultVendorId",
  "description",
  "sku",
  "unit",
  "unitCost",
  "currencyCode",
  "lowStockThreshold",
  "trackLowStock",
  "minOrderQty",
  "notes",
  "barcodes",
];

/** The fields "More details" folds away on a NEW item — an error on any of them opens it. */
const MORE_DETAIL_FIELDS = ["categoryId", "description", "sku", "lowStockThreshold", "trackLowStock", "minOrderQty", "notes"];

const DEFAULT_UNIT = "each";

const getInitialFormData = (): FormData => ({
  name: "",
  categoryId: null,
  defaultVendorId: null,
  description: "",
  sku: "",
  unit: DEFAULT_UNIT,
  unitCost: null,
  currencyCode: null,
  lowStockThreshold: null,
  trackLowStock: false,
  minOrderQty: null,
  notes: "",
  barcodes: [],
  status: STATUSES.ACTIVE,
});

const fromRecord = (record: InventoryItem): FormData => ({
  name: record.name || "",
  categoryId: record.categoryId || null,
  defaultVendorId: record.defaultVendorId || null,
  description: record.description || "",
  sku: record.sku || "",
  unit: record.unit || DEFAULT_UNIT,
  unitCost: record.unitCost ?? null,
  currencyCode: record.currencyCode || null,
  lowStockThreshold: record.lowStockThreshold ?? null,
  trackLowStock: record.trackLowStock ?? false,
  minOrderQty: record.minOrderQty ?? null,
  notes: record.notes || "",
  barcodes: (record.barcodes ?? []).map((b) => ({
    id: b.id,
    code: b.code || "",
    label: b.label || "",
    unitsPerCode: b.unitsPerCode ?? 1,
  })),
  status: record.status ?? STATUSES.ACTIVE,
});

const serialize = (data: FormData): string => JSON.stringify(data);

const InventoryItemEditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
  </Stack>
);

export const InventoryItemEditDrawer: React.FC<InventoryItemEditDrawerProps> = ({
  opened,
  onClose,
  recordId,
  initialName,
  prefill,
  contextEventId,
  onCreated,
  onUpdated,
}) => {
  const { t } = useTranslation(["inventory", "common"]);

  // Prefill via ref so a fresh object identity doesn't re-seed the form mid-edit.
  const prefillRef = useRef(prefill);
  prefillRef.current = prefill;

  const ctx = useOptionalInventoryItemsContext();
  const createFn = ctx?.create;
  const updateFn = ctx?.update;
  const creating = ctx?.isCreating ?? false;
  const updating = ctx?.isUpdating ?? false;
  const operationError = ctx?.lastError ?? null;
  const clearOperationError = ctx?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  const clearErrRef = useRef(clearOperationError);
  clearErrRef.current = clearOperationError;

  // ── Self-loading (via the service directly, so it works with or without a page list) ──
  const [record, setRecord] = useState<InventoryItem | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);
  // The drawer keeps the result of its OWN save, so a refusal is shown even when the caller's
  // context is not the one that ran it (em-scaffold-ui: a drawer must not rely on lastError alone).
  const [submitError, setSubmitError] = useState<OpResult | null>(null);
  const [showMore, setShowMore] = useState(false);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  useEffect(() => {
    let isCancelled = false;

    if (opened && isEditMode && recordId) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      InventoryItemService.get(recordId).then((result) => {
        if (isCancelled) {
          return;
        }

        if (result.didSucceed()) {
          setRecord(result.getDataFirst() as InventoryItem);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(result);
        }

        setIsLoadingRecord(false);
      });
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [opened, isEditMode, recordId]);

  // ── Form state ──
  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }

    setIsDirty(serialize(formData) !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  const effectiveError = loadError || submitError || operationError;

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  // Every open starts folded for a NEW item and unfolded for an existing one.
  useEffect(() => {
    if (opened) {
      setShowMore(isEditMode);
      setSubmitError(null);
    }
  }, [opened, isEditMode]);

  // ⚠ A refusal on a folded field must be SEEN — open the section rather than leave the message on
  // a control that is not rendered (the "save just does nothing" family).
  const moreHasError = MORE_DETAIL_FIELDS.some((name) => !!fieldErrors[name]);

  useEffect(() => {
    if (moreHasError) {
      setShowMore(true);
    }
  }, [moreHasError]);

  // ⚠ And it cannot be folded away while one of its fields still shows an error.
  const foldOpen = showMore || moreHasError;

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (isEditMode && !record) {
      return;
    }

    let newFormData: FormData;

    if (record) {
      newFormData = fromRecord(record);
    } else {
      // Create mode — seed from prefill (scanned code / product lookup) then initialName.
      const pf = prefillRef.current;
      newFormData = {
        ...getInitialFormData(),
        name: (pf?.name || initialName || "").trim(),
        description: pf?.description?.trim() || "",
        barcodes: pf?.barcodeCode ? [{ code: pf.barcodeCode.trim(), label: "", unitsPerCode: 1 }] : [],
      };
    }

    setFormData(newFormData);
    initialSnapshotRef.current = serialize(newFormData);
    setIsDirty(false);
    clearErrRef.current?.();
  }, [opened, record, isEditMode, initialName]);

  const setField = useCallback(<K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // ── SKU auto-generate ──
  const [suggestingSku, setSuggestingSku] = useState(false);

  const handleSuggestSku = useCallback(async () => {
    setSuggestingSku(true);

    try {
      const result = await InventoryItemService.suggestSku(contextEventId);

      if (result.code === OP_RESULT_CODES.OK) {
        const sku = (result.getDataFirst() as { sku?: string } | undefined)?.sku;

        if (sku) {
          setField("sku", sku);
        }
      }
    } finally {
      setSuggestingSku(false);
    }
  }, [contextEventId, setField]);

  // Add the SKU as a scannable barcode so a printed label resolves back to this item.
  const skuAlreadyBarcode = useMemo(
    () => !!formData.sku.trim() && formData.barcodes.some((b) => (b.code || "").trim() === formData.sku.trim()),
    [formData.sku, formData.barcodes]
  );

  const handleUseSkuAsBarcode = useCallback(() => {
    const sku = formData.sku.trim();
    if (!sku || skuAlreadyBarcode) {
      return;
    }

    setField("barcodes", [...formData.barcodes, { code: sku, label: "", unitsPerCode: 1 }]);
  }, [formData.sku, formData.barcodes, skuAlreadyBarcode, setField]);

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    clearErrRef.current?.();
    setLoadError(null);
    setSubmitError(null);

    // Send only barcodes that have a code; keep ids so the server diffs in place.
    const barcodes = formData.barcodes
      .filter((b) => (b.code || "").trim())
      .map((b) => ({
        id: b.id,
        code: (b.code || "").trim(),
        label: (b.label || "").trim() || null,
        unitsPerCode: b.unitsPerCode && b.unitsPerCode >= 1 ? b.unitsPerCode : 1,
      }));

    const submitData: InventoryItemInput = {
      name: formData.name.trim() || null,
      categoryId: formData.categoryId,
      defaultVendorId: formData.defaultVendorId,
      description: formData.description.trim() || null,
      sku: formData.sku.trim() || null,
      unit: formData.unit.trim() || DEFAULT_UNIT,
      unitCost: formData.unitCost,
      currencyCode: formData.currencyCode,
      lowStockThreshold: formData.lowStockThreshold,
      trackLowStock: formData.trackLowStock,
      minOrderQty: formData.minOrderQty,
      notes: formData.notes.trim() || null,
      barcodes,
      status: formData.status,
    };

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);
      const saved = result.data ? (result.data as InventoryItem[])[0] : null;

      if (saved) {
        if (isCreate) {
          onCreated?.(saved);
        } else {
          onUpdated?.(saved);
        }
      }

      onClose();

      return;
    }

    if (result) {
      setSubmitError(result);
    }
  }, [formData, isEditMode, recordId, createFn, updateFn, onClose, onCreated, onUpdated]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord,
    enabled: opened,
  });

  const drawerTitle = isEditMode
    ? t("inventory:items.form.editTitle", { defaultValue: "Edit inventory item" })
    : t("inventory:items.form.addTitle", { defaultValue: "Add inventory item" });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="lg"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <InventoryItemEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          {!isEditMode && prefill && (prefill.imageUrl || prefill.categoryHint) ? (
            <Group gap="sm" wrap="nowrap" align="center" p="xs" style={{ background: "var(--mantine-color-blue-light)", borderRadius: 8 }}>
              {prefill.imageUrl ? (
                <Image src={prefill.imageUrl} w={44} h={44} radius="sm" fit="contain" alt="" />
              ) : (
                <AppIcon iconName="Package" size={22} />
              )}
              <Box style={{ minWidth: 0 }}>
                <Text size="md" fw={600}>
                  {t("inventory:items.form.prefillHint.title", { defaultValue: "Pre-filled from a product lookup" })}
                </Text>
                {prefill.categoryHint ? (
                  <Text size="sm" c="dimmed" lineClamp={1}>
                    {t("inventory:items.form.prefillHint.category", { defaultValue: "Product category: {{c}} — you can set a Category under More details.", c: prefill.categoryHint })}
                  </Text>
                ) : null}
              </Box>
            </Group>
          ) : null}

          <TextInput
            label={t("inventory:items.form.fields.name.label", { defaultValue: "Name" })}
            placeholder={t("inventory:items.form.fields.name.placeholder", { defaultValue: "e.g. Bottled water" })}
            value={formData.name}
            onChange={(e) => setField("name", e.currentTarget.value)}
            error={fieldErrors.name}
            required
            data-autofocus
            size="md"
          />

          <VendorSelect
            label={t("inventory:items.form.fields.defaultVendor.label", { defaultValue: "Default vendor" })}
            description={t("inventory:items.form.fields.defaultVendor.description", {
              defaultValue: "Filled in for you when you receive this item.",
            })}
            value={formData.defaultVendorId}
            onChange={(id) => setField("defaultVendorId", id)}
            fallbackLabel={record?.defaultVendorLabel}
            error={fieldErrors.defaultVendorId}
          />

          <Divider
            label={t("inventory:items.form.sections.barcodes", { defaultValue: "Barcodes" })}
            labelPosition="left"
          />

          <InventoryBarcodesEditor
            value={formData.barcodes}
            onChange={(barcodes) => setField("barcodes", barcodes)}
            error={fieldErrors.barcodes}
          />

          <Divider
            label={t("inventory:items.form.sections.costing", { defaultValue: "Costing & stock" })}
            labelPosition="left"
          />

          <SimpleGrid cols={{ base: 1, xs: 3 }} spacing="sm" verticalSpacing="sm">
            <TextInput
              label={t("inventory:items.form.fields.unit.label", { defaultValue: "Unit" })}
              placeholder={DEFAULT_UNIT}
              value={formData.unit}
              onChange={(e) => setField("unit", e.currentTarget.value)}
              error={fieldErrors.unit}
              size="md"
            />
            <MoneyInput
              label={t("inventory:items.form.fields.unitCost.label", { defaultValue: "Unit cost" })}
              value={formData.unitCost}
              onChange={(v) => setField("unitCost", v)}
              currency={formData.currencyCode}
              error={fieldErrors.unitCost}
            />
            <CurrencySelector
              label={t("inventory:items.form.fields.currency.label", { defaultValue: "Currency" })}
              value={formData.currencyCode}
              onChange={(_name, value) => setField("currencyCode", value)}
              error={fieldErrors.currencyCode}
              isoOnly
              withinPortal
              size="md"
            />
          </SimpleGrid>

          <Button
            variant="subtle"
            color="gray"
            size="md"
            justify="space-between"
            fullWidth
            onClick={() => setShowMore((v) => !v)}
            rightSection={<AppIcon iconName={foldOpen ? "ChevronUp" : "ChevronDown"} size={16} />}
            aria-expanded={foldOpen}
          >
            {foldOpen
              ? t("inventory:items.form.lessDetails", { defaultValue: "Fewer details" })
              : t("inventory:items.form.moreDetails", {
                  defaultValue: "More details — category, SKU, stock alerts, notes",
                })}
          </Button>

          <Collapse in={foldOpen}>
            <Stack gap="md">
              <CategorySelect
                group="inventory"
                label={t("inventory:items.form.fields.category.label", { defaultValue: "Category" })}
                description={t("inventory:items.form.fields.category.description", {
                  defaultValue: "Group similar supplies (drinks, decor, gear…).",
                })}
                value={formData.categoryId}
                onChange={(id) => setField("categoryId", id)}
                error={fieldErrors.categoryId}
                clearable
                size="md"
              />

              <Textarea
                label={t("inventory:items.form.fields.description.label", { defaultValue: "Description" })}
                placeholder={t("inventory:items.form.fields.description.placeholder", { defaultValue: "Optional" })}
                value={formData.description}
                onChange={(e) => setField("description", e.currentTarget.value)}
                error={fieldErrors.description}
                autosize
                minRows={1}
                maxRows={3}
                size="md"
              />

              <TextInput
                label={t("inventory:items.form.fields.sku.label", { defaultValue: "SKU" })}
                placeholder={t("inventory:items.form.fields.sku.placeholder", { defaultValue: "Optional code" })}
                value={formData.sku}
                onChange={(e) => setField("sku", e.currentTarget.value)}
                error={fieldErrors.sku}
                size="md"
                rightSection={
                  <Tooltip label={t("inventory:items.form.fields.sku.suggest", { defaultValue: "Auto-generate a SKU" })} withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="grape"
                      onClick={handleSuggestSku}
                      loading={suggestingSku}
                      aria-label={t("inventory:items.form.fields.sku.suggest", { defaultValue: "Auto-generate a SKU" })}
                    >
                      <AppIcon iconName="Wand" size={16} />
                    </ActionIcon>
                  </Tooltip>
                }
              />

              <Group justify="flex-start">
                <Button
                  variant="subtle"
                  size="compact-md"
                  leftSection={<AppIcon iconName="Qrcode" size={16} />}
                  disabled={!formData.sku.trim() || skuAlreadyBarcode}
                  onClick={handleUseSkuAsBarcode}
                >
                  {skuAlreadyBarcode
                    ? t("inventory:items.form.skuIsBarcode", { defaultValue: "SKU is a barcode" })
                    : t("inventory:items.form.useSkuAsBarcode", { defaultValue: "Use SKU as barcode" })}
                </Button>
                <Text size="sm" c="dimmed">
                  {t("inventory:items.form.useSkuAsBarcode.hint", { defaultValue: "Makes a printed label scan back to this item." })}
                </Text>
              </Group>

              <Group grow align="flex-start">
                <NumberField
                  label={t("inventory:items.form.fields.lowStockThreshold.label", { defaultValue: "Low-stock / reorder point" })}
                  description={t("inventory:items.form.fields.lowStockThreshold.description", {
                    defaultValue: "Alert (and reorder) when total on-hand drops to this. Optional.",
                  })}
                  placeholder={t("inventory:items.form.fields.lowStockThreshold.placeholder", { defaultValue: "Optional" })}
                  value={formData.lowStockThreshold}
                  onChange={(_n, v) => setField("lowStockThreshold", v)}
                  min={0}
                  allowNegative={false}
                  error={fieldErrors.lowStockThreshold}
                  size="md"
                />
                <Switch
                  mt="xl"
                  label={t("inventory:items.form.fields.trackLowStock.label", { defaultValue: "Track low stock" })}
                  checked={formData.trackLowStock}
                  onChange={(e) => setField("trackLowStock", e.currentTarget.checked)}
                  error={fieldErrors.trackLowStock}
                  size="md"
                />
              </Group>

              <NumberField
                label={t("inventory:items.form.fields.minOrderQty.label", { defaultValue: "Minimum order quantity" })}
                description={t("inventory:items.form.fields.minOrderQty.description", {
                  defaultValue: "How much to buy when reordering. Used for purchase-order buy-lists. Optional.",
                })}
                placeholder={t("inventory:items.form.fields.minOrderQty.placeholder", { defaultValue: "Optional" })}
                value={formData.minOrderQty}
                onChange={(_n, v) => setField("minOrderQty", v)}
                min={0}
                allowNegative={false}
                error={fieldErrors.minOrderQty}
                size="md"
              />

              <Textarea
                label={t("inventory:items.form.fields.notes.label", { defaultValue: "Notes" })}
                placeholder={t("inventory:items.form.fields.notes.placeholder", {
                  defaultValue: "Storage, handling, supplier details…",
                })}
                value={formData.notes}
                onChange={(e) => setField("notes", e.currentTarget.value)}
                error={fieldErrors.notes}
                autosize
                minRows={2}
                maxRows={5}
                size="md"
              />
            </Stack>
          </Collapse>
        </Stack>
      )}
    </AppDrawer>
  );
};

export default InventoryItemEditDrawer;
